import { Button, Card, Col, Grid, Progress, Row, Tag, Typography } from 'antd';
import React, { useContext } from 'react';
import '../vehicleOverview.css';
import { GiCarWheel } from 'react-icons/gi';
import { IoMdRefresh } from 'react-icons/io';
import { LuLoader } from 'react-icons/lu';
import { PiEngineDuotone, PiEngineFill, PiEngine, PiSeatbeltBold } from 'react-icons/pi';
import { BsLightbulb, BsLightbulbFill } from 'react-icons/bs';
import { TbAirConditioningDisabled, TbAirConditioning } from 'react-icons/tb';
import { IoMdLock, IoMdUnlock } from 'react-icons/io';
import { CarTopView, TireMarker } from 'style/vehicleOverviewStyle';
import LocateMyVehicle from './locationBasedServices/LocateMyVehicle';
import { SimulatorContext } from 'store/context';
import { useQuery } from '@tanstack/react-query';
import { getCompanionVehicleDetails, getShadowState } from 'services';
import moment from 'moment';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const getBatteryColor = (level, charging) => {
  if (charging) return '#10b981';
  if (level <= 20) return '#ef4444';
  if (level <= 40) return '#f97316';
  if (level <= 80) return '#eab308';
  return '#10b981';
};

const getBatteryStatus = (level, charging) => {
  if (level >= 100) return 'Fully Charged';
  if (charging) return 'Charging…';
  if (level <= 20) return 'Low Battery';
  return null;
};

const getTempStatus = (temp) => {
  if (temp < 40) return { color: '#10b981', label: 'Normal' };
  if (temp < 70) return { color: '#f59e0b', label: 'Warm' };
  return { color: '#ef4444', label: 'Hot' };
};


// Chip used for the icon in the card header and for seatbelt/door indicators
const IconChip = ({ icon, bg = '#f1f5f9', color = '#94a3b8', size = 36 }) => (
  <div
    style={{
      width: size,
      height: size,
      borderRadius: 8,
      background: bg,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color,
      flexShrink: 0,
      // font-size inherited by the SVG icon via react-icons' 1em sizing
      fontSize: Math.round(size * 0.52),
    }}
  >
    {icon}
  </div>
);

// Level badge row — replaces stacked indicator icons with readable chips
const LevelBadges = ({ levels }) => (
  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 8 }}>
    {levels.map(({ label, active, color }) => (
      <span
        key={label}
        style={{
          padding: '2px 8px',
          borderRadius: 5,
          fontSize: 10,
          fontWeight: 600,
          background: active ? `${color}18` : '#f1f5f9',
          color: active ? color : '#94a3b8',
          border: `1px solid ${active ? color + '55' : '#e2e8f0'}`,
          transition: 'all 0.2s',
        }}
      >
        {label}
      </span>
    ))}
  </div>
);

// Base card — header row (label + optional icon chip) + content area below
const MetricCard = ({ label, icon, iconBg, iconColor, alert, children }) => (
  <Card
    style={{
      width: '100%',
      height: '100%',
      borderRadius: 12,
      border: alert ? '1px solid #fca5a5' : '1px solid #e2e8f0',
      background: alert ? '#fff5f5' : '#ffffff',
    }}
    styles={{ body: { padding: '14px 16px', height: '100%', display: 'flex', flexDirection: 'column', gap: 10 } }}
  >
    {/* card header */}
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
      <span
        style={{
          fontSize: 10,
          fontWeight: 600,
          color: '#94a3b8',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
        }}
      >
        {label}
      </span>
      {icon && (
        <IconChip icon={icon} bg={iconBg} color={iconColor} size={34} />
      )}
    </div>

    {/* card content */}
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 4 }}>
      {children}
    </div>
  </Card>
);

const CardWrapper = ({ children, sm = 12, md = 12, lg = 8 }) => (
  <Col xs={24} sm={sm} md={md} lg={lg} style={{ display: 'flex' }}>
    {children}
  </Col>
);

const DOOR_POSITIONS = [
  ['FRONT_LEFT', 'FL'],
  ['FRONT_RIGHT', 'FR'],
  ['REAR_LEFT', 'RL'],
  ['REAR_RIGHT', 'RR'],
];
const FRONT_BELTS = [['FRONT_LEFT', 'FL'], ['FRONT_RIGHT', 'FR']];
const REAR_BELTS = [['REAR_LEFT', 'RL'], ['REAR_MIDDLE', 'RM'], ['REAR_RIGHT', 'RR']];

const VehicleOverview = () => {
  const screens = useBreakpoint();
  const isMobile = screens.md === false;

  const { vehicleSimulatorSettings: { vehicleKey, serialNo }, valetMode } = useContext(SimulatorContext);

  const {
    data: {
      data: {
        telematicsData: {
          batteryLevel = 0,
          charged = false,
          engineRpm = 0,
          leftFrontTyrePressure = 0,
          leftRearTyrePressure = 0,
          rightFrontTyrePressure = 0,
          rightRearTyrePressure = 0,
          speed = 0,
          temperature = 0,
        } = {},
      } = {},
    } = {},
    dataUpdatedAt,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ['getCompanionVehicleDetails', vehicleKey],
    queryFn: getCompanionVehicleDetails,
    refetchIntervalInBackground: true,
    refetchInterval: 30000,
    retry: 1,
  });

  const { data: { data: { payload = '' } = {} } = {} } = useQuery({
    queryKey: ['getShadowState', serialNo],
    queryFn: getShadowState,
    refetchInterval: 30000,
    refetchIntervalInBackground: true,
    retry: 1,
  });

  const shadowState = payload ? JSON.parse(payload) : null;
  const {
    state: {
      desired: {
        remoteFunctions: {
          lampControl = {},
          airCondition = {},
          seatBeltStatus = {},
          doorControl = {},
        } = {},
      } = {},
    } = {},
  } = shadowState || {};

  const batteryColor = getBatteryColor(batteryLevel, charged);
  const batteryStatus = getBatteryStatus(batteryLevel, charged);
  const tempStatus = getTempStatus(temperature);

  const lampOn = lampControl?.action === 'ON';
  const acOn = airCondition?.action === 'ON';

  return (
    <>
      {/* page header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>Vehicle Overview</Title>
          {serialNo && <Text style={{ fontSize: 12, color: '#94a3b8', fontFamily: 'monospace' }}>{serialNo}</Text>}
        </div>
        <Button
          type="primary"
          icon={<IoMdRefresh />}
          loading={(isLoading || isFetching) ? { icon: <LuLoader /> } : false}
        >
          Auto Refresh (30s)
        </Button>
      </div>

      <Row gutter={[16, 16]}>

        {/* ── left: metric cards ── */}
        <Col xs={24} lg={15} xl={16}>
          <Row gutter={[12, 12]}>

            {/* Battery */}
            <CardWrapper sm={24} md={24} lg={12}>
              <MetricCard label="Battery Level">
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 30, fontWeight: 700, color: batteryColor, lineHeight: 1 }}>
                    {batteryLevel}%
                  </span>
                  {batteryStatus && (
                    <span style={{ fontSize: 12, color: '#64748b' }}>{batteryStatus}</span>
                  )}
                </div>
                <Progress
                  percent={batteryLevel}
                  strokeColor={batteryColor}
                  trailColor="#f1f5f9"
                  size="small"
                  showInfo={false}
                  style={{ marginTop: 6 }}
                />
              </MetricCard>
            </CardWrapper>

            {/* Speed */}
            <CardWrapper>
              <MetricCard
                label="Top Speed"
                icon={<GiCarWheel className={speed > 0 ? 'spinning' : ''} />}
                iconBg="#eff6ff"
                iconColor="#2563eb"
              >
                <span style={{ fontSize: 30, fontWeight: 700, color: '#0f172a', lineHeight: 1 }}>
                  {speed}
                </span>
                <span style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>km/h</span>
              </MetricCard>
            </CardWrapper>

            {/* Engine RPM */}
            <CardWrapper>
              <MetricCard
                label="Engine RPM"
                icon={<PiEngine />}
                iconBg="#f5f3ff"
                iconColor="#7c3aed"
              >
                <span style={{ fontSize: 30, fontWeight: 700, color: '#0f172a', lineHeight: 1 }}>
                  {engineRpm}
                </span>
                <span style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>r/min</span>
                <LevelBadges
                  levels={[
                    { label: 'Low', active: engineRpm > 0 && engineRpm < 1000, color: '#ef4444' },
                    { label: '1-3K', active: engineRpm >= 1000 && engineRpm < 3000, color: '#f59e0b' },
                    { label: 'High', active: engineRpm >= 3000, color: '#10b981' },
                  ]}
                />
              </MetricCard>
            </CardWrapper>

            {/* Temperature */}
            <CardWrapper>
              <MetricCard
                label="Engine Temperature"
                icon={<PiEngineFill />}
                iconBg={`${tempStatus.color}18`}
                iconColor={tempStatus.color}
              >
                <span style={{ fontSize: 30, fontWeight: 700, color: tempStatus.color, lineHeight: 1 }}>
                  {temperature}
                </span>
                <span style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>°C</span>
                <LevelBadges
                  levels={[
                    { label: 'Normal', active: temperature < 40, color: '#10b981' },
                    { label: 'Warm', active: temperature >= 40 && temperature < 70, color: '#f59e0b' },
                    { label: 'Hot', active: temperature >= 70, color: '#ef4444' },
                  ]}
                />
              </MetricCard>
            </CardWrapper>

            {/* Lamp */}
            <CardWrapper>
              <MetricCard
                label="Lamp Status"
                icon={lampOn ? <BsLightbulbFill /> : <BsLightbulb />}
                iconBg={lampOn ? '#fefce8' : '#f8fafc'}
                iconColor={lampOn ? '#ca8a04' : '#94a3b8'}
              >
                <Tag
                  color={lampOn ? 'success' : 'default'}
                  style={{ borderRadius: 6, fontSize: 14, padding: '3px 12px', fontWeight: 600, display: 'inline-flex', alignItems: 'center' }}
                >
                  {lampOn ? 'ON' : 'OFF'}
                </Tag>
              </MetricCard>
            </CardWrapper>

            {/* AC */}
            <CardWrapper>
              <MetricCard
                label="Air Condition"
                icon={acOn ? <TbAirConditioning /> : <TbAirConditioningDisabled />}
                iconBg={acOn ? '#eff6ff' : '#f8fafc'}
                iconColor={acOn ? '#2563eb' : '#94a3b8'}
              >
                <Tag
                  color={acOn ? 'processing' : 'default'}
                  style={{ borderRadius: 6, fontSize: 14, padding: '3px 12px', fontWeight: 600, display: 'inline-flex', alignItems: 'center' }}
                >
                  {acOn ? 'ON' : 'OFF'}
                </Tag>
              </MetricCard>
            </CardWrapper>

            {/* Seat belts — front */}
            <CardWrapper>
              <MetricCard label="Seat Belts — Front">
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  {FRONT_BELTS.map(([key, label]) => {
                    const fastened = seatBeltStatus?.[key] !== 'UNFASTENED';
                    return (
                      <div key={key} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5 }}>
                        <IconChip
                          icon={<PiSeatbeltBold />}
                          bg={fastened ? '#f0fdf4' : '#f8fafc'}
                          color={fastened ? '#10b981' : '#cbd5e1'}
                          size={40}
                        />
                        <span style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600 }}>{label}</span>
                      </div>
                    );
                  })}
                </div>
              </MetricCard>
            </CardWrapper>

            {/* Seat belts — rear */}
            <CardWrapper>
              <MetricCard label="Seat Belts — Rear">
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  {REAR_BELTS.map(([key, label]) => {
                    const fastened = seatBeltStatus?.[key] !== 'UNFASTENED';
                    return (
                      <div key={key} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5 }}>
                        <IconChip
                          icon={<PiSeatbeltBold />}
                          bg={fastened ? '#f0fdf4' : '#f8fafc'}
                          color={fastened ? '#10b981' : '#cbd5e1'}
                          size={40}
                        />
                        <span style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600 }}>{label}</span>
                      </div>
                    );
                  })}
                </div>
              </MetricCard>
            </CardWrapper>

            {/* Door locks */}
            <CardWrapper>
              <MetricCard label="Door Locks">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                  {DOOR_POSITIONS.map(([key, label]) => {
                    const unlocked = doorControl?.[key] === 'UNLOCK';
                    return (
                      <div
                        key={key}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          padding: '6px 10px',
                          borderRadius: 8,
                          background: unlocked ? '#f8fafc' : '#f0fdf4',
                          border: `1px solid ${unlocked ? '#e2e8f0' : '#86efac'}`,
                        }}
                      >
                        {unlocked
                          ? <IoMdUnlock style={{ fontSize: 16, color: '#94a3b8', flexShrink: 0 }} />
                          : <IoMdLock style={{ fontSize: 16, color: '#10b981', flexShrink: 0 }} />
                        }
                        <span style={{ fontSize: 11, fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' }}>
                          {label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </MetricCard>
            </CardWrapper>

            {/* Valet mode */}
            <CardWrapper>
              <MetricCard
                label="Valet Mode"
                alert={valetMode?.enabled}
                icon={<IoMdLock />}
                iconBg={valetMode?.enabled ? '#fee2e2' : '#f1f5f9'}
                iconColor={valetMode?.enabled ? '#ef4444' : '#94a3b8'}
              >
                <Tag
                  color={valetMode?.enabled ? 'error' : 'default'}
                  style={{ borderRadius: 6, fontSize: 14, padding: '3px 12px', fontWeight: 600, display: 'inline-flex', alignItems: 'center' }}
                >
                  {valetMode?.enabled ? 'ACTIVE' : 'OFF'}
                </Tag>
                {valetMode?.enabled && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginTop: 8 }}>
                    <Text style={{ fontSize: 12, color: '#dc2626' }}>Max: {valetMode.maxSpeed} km/h</Text>
                    <Text style={{ fontSize: 12, color: '#dc2626' }}>
                      Trunk: {valetMode.trunkLocked ? 'Locked' : 'Unlocked'}
                    </Text>
                  </div>
                )}
              </MetricCard>
            </CardWrapper>

            {/* Map */}
            <Col xs={24}>
              {/* <LocateMyVehicle /> */}
            </Col>

          </Row>
        </Col>

        {/* ── right: tyre pressure ── */}
        <Col xs={24} lg={9} xl={8} style={{ display: 'flex' }}>
          <Card
            style={{ width: '100%', borderRadius: 12, border: '1px solid #e2e8f0' }}
            styles={{ body: { padding: isMobile ? '14px 12px' : '16px', display: 'flex', flexDirection: 'column', gap: 12 } }}
          >
            {/* header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
              <span style={{ fontSize: 10, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Tyre Pressure
              </span>
              <IconChip icon={<GiCarWheel />} bg="#eff6ff" color="#2563eb" size={34} />
            </div>

            {/* car top-view diagram */}
            <CarTopView className="car-top-view">
              <TireMarker {...(leftFrontTyrePressure <= 30 && { warning: true })} className="front-left"><span>FL</span></TireMarker>
              <TireMarker {...(rightFrontTyrePressure <= 30 && { warning: true })} className="front-right"><span>FR</span></TireMarker>
              <TireMarker {...(leftRearTyrePressure <= 30 && { warning: true })} className="rear-left"><span>RL</span></TireMarker>
              <TireMarker {...(rightRearTyrePressure <= 30 && { warning: true })} className="rear-right"><span>RR</span></TireMarker>
            </CarTopView>

            {/* 2×2 pressure grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {[
                { label: 'Front Left', value: leftFrontTyrePressure },
                { label: 'Front Right', value: rightFrontTyrePressure },
                { label: 'Rear Left', value: leftRearTyrePressure },
                { label: 'Rear Right', value: rightRearTyrePressure },
              ].map(({ label, value }) => {
                const warn = value <= 30;
                return (
                  <div
                    key={label}
                    style={{
                      padding: '10px 12px',
                      paddingBottom: 28,
                      borderRadius: 8,
                      background: warn ? '#fff5f5' : '#f0f9ff',
                      border: `1px solid ${warn ? '#fca5a5' : '#bfdbfe'}`,
                      position: 'relative',
                      transition: 'background 0.3s, border-color 0.3s',
                    }}
                  >
                    <div style={{ fontSize: 10, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
                      {label}
                    </div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: warn ? '#ef4444' : '#0f172a', lineHeight: 1 }}>
                      {value} <span style={{ fontSize: 11, fontWeight: 500, color: '#94a3b8' }}>psi</span>
                    </div>
                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>— °C</div>
                    <div style={{ position: 'absolute', bottom: 7, right: 9, fontSize: 10, color: '#94a3b8', whiteSpace: 'nowrap' }}>
                      {moment(dataUpdatedAt).fromNow()}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* spare tyre */}
            <div
              style={{
                padding: '10px 14px',
                borderRadius: 8,
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Spare
              </span>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#94a3b8' }}>— psi</span>
            </div>
          </Card>
        </Col>

      </Row>
    </>
  );
};

export default VehicleOverview;
